import React, { useState, useEffect, useRef, useContext } from "react";
import { Controller, useWatch } from "react-hook-form";
import { connectTheme } from "@contexts/Context";

const SearchableDropdown = ({
  name = "",
  label = "",
  required = false,
  isLoading = false,
  loadingText = "Loading Options...",
  errorMessage,
  placeholder = "",
  defaultValue = "",
  options = [],
  control,
  rules,
  disabled = false,
  onTypeAheadChange,
}) => {
  const { isLight } = useContext(connectTheme);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [debouncedValue, setDebouncedValue] = useState("");

  const itemClassName = `select_item ${isLight ? "bg_light" : "bg_dark"}`;
  const selectClassName = `react-select select select_menu ${isLight ? "border_light color_light" : "border_dark color_dark"}`;

  const seeded = useRef(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      onTypeAheadChange?.(debouncedValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedValue]);

  //Watch the form’s current value for this field
  const watchedValue = useWatch({ control, name, defaultValue });

  //Seed or clear inputValue whenever watchedValue changes
  useEffect(() => {
    if (!seeded.current && watchedValue) {
      const opt = options.find((o) => o.value === watchedValue);
      if (opt) setInputValue(opt.label);
      seeded.current = true;
    }
  }, [watchedValue, options]);

  // Clear when the form value goes empty
  useEffect(() => {
    if (seeded.current && !watchedValue) {
      setInputValue("");
    }
  }, [watchedValue]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field, formState: { errors } }) => {
        const handleChange = (e) => {
          const val = e.target.value;
          setInputValue(val);
          setDebouncedValue(val);
          setShowDropdown(true);
        };
        const handleSelect = (value, label) => {
          field.onChange(value);
          setInputValue(label);
          setDebouncedValue("");
          setShowDropdown(false);
        };
        const handleBlur = () => {
          field.onChange(inputValue);
        };

        const filteredOptions = options.filter((opt) =>
          opt.label.toLowerCase().includes(inputValue.toLowerCase())
        );

        return (
          <div
            className="form__form-group-input-wrap--error-above form__form-group searchable-dropdown-wrapper"
            ref={dropdownRef}
          >
            {label && (
              <div className="form__form-group-label searchable-dropdown-label">
                {label}
                {required && <span className="text-danger"> *</span>}
              </div>
            )}
            <div className="form__form-group-field">
              <div className="searchable-dropdown-inner">
                <input
                  type="text"
                  name={name}
                  className={selectClassName}
                  placeholder={
                    defaultValue && inputValue === ""
                      ? "Please type any letter"
                      : ""
                  }
                  value={inputValue}
                  disabled={disabled}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={() => {
                    setShowDropdown(true);
                    if (inputValue.trim() !== "") {
                      setDebouncedValue(inputValue);
                    }
                  }}
                />

                {showDropdown && (
                  <div
                    className={`select_dropdown ${!isLight ? "select_dropdown--dark" : ""}`}
                  >
                    {isLoading ? (
                      <div className={itemClassName}>{loadingText}</div>
                    ) : filteredOptions.length === 0 ? (
                      <div className={itemClassName}>No results found</div>
                    ) : (
                      filteredOptions.map(({ value, label }) => (
                        <div
                          key={value}
                          className={itemClassName}
                          onClick={() => handleSelect(value, label)}
                        >
                          {label}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {errors?.[name] && (
                <span className="form__form-group-error">
                  {errors[name]?.message}
                </span>
              )}
            </div>
          </div>
        );
      }}
    />
  );
};

export default SearchableDropdown;
